import React, { useState, useEffect, useContext } from 'react';
import {
  Container,
  Paper,
  Typography,
  Button,
  ButtonGroup,
  Box,
  CircularProgress,
  Snackbar
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { Public as PublicIcon, GetApp as DownloadIcon } from '@material-ui/icons';
import { toast } from 'react-toastify';
import { saveAs } from 'file-saver';

import MainContainer from '../../components/MainContainer';
import MainHeader from '../../components/MainHeader';
import MainHeaderButtonsWrapper from '../../components/MainHeaderButtonsWrapper';
import Title from '../../components/Title';
import StatisticsTable from '../../components/StatisticsTable';
import ChartVisualization from '../../components/ChartVisualization';
import DateRangeFilter from '../../components/DateRangeFilter';
import api from '../../services/api';
import { AuthContext } from '../../context/Auth/AuthContext';

const useStyles = makeStyles((theme) => ({
  mainPaper: {
    flex: 1,
    padding: theme.spacing(2),
    overflowY: 'scroll',
    ...theme.scrollbarStyles
  },
  chartTypeButtons: {
    marginBottom: theme.spacing(2)
  },
  exportButtons: {
    marginTop: theme.spacing(2),
    display: 'flex',
    gap: theme.spacing(1)
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '400px'
  }
}));

const GeolocationPage = () => {
  const classes = useStyles();
  const { user } = useContext(AuthContext);

  const [statistics, setStatistics] = useState([]);
  const [loading, setLoading] = useState(false);
  const [chartType, setChartType] = useState('bar');
  const [dateRange, setDateRange] = useState({ start: null, end: null });
  const [error, setError] = useState('');

  // Cargar estadísticas
  const loadStatistics = async () => {
    try {
      setLoading(true);
      setError('');

      const params = {
        companyId: user.companyId
      };

      if (dateRange.start) {
        params.startDate = dateRange.start.toISOString();
      }

      if (dateRange.end) {
        params.endDate = dateRange.end.toISOString();
      }

      const { data } = await api.get('/geolocation/statistics', { params });
      setStatistics(data.statistics);
    } catch (err) {
      console.error('Error al cargar estadísticas:', err);
      const errorMessage = err.response?.data?.error || 'Error al cargar las estadísticas de geolocalización';
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // Cargar estadísticas al montar y cuando cambia el rango de fechas
  useEffect(() => {
    loadStatistics();
  }, [dateRange]);

  // Manejar cambio de rango de fechas
  const handleDateChange = (start, end) => {
    setDateRange({ start, end });
  };

  // Manejar exportación
  const handleExport = async (format) => {
    try {
      const params = {
        companyId: user.companyId,
        format
      };

      if (dateRange.start) {
        params.startDate = dateRange.start.toISOString();
      }

      if (dateRange.end) {
        params.endDate = dateRange.end.toISOString();
      }

      const response = await api.get('/geolocation/export', {
        params,
        responseType: 'blob'
      });

      // Obtener nombre de archivo del header o generar uno
      const timestamp = new Date().toISOString().split('T')[0];
      const filename = `geolocation_statistics_${timestamp}.${format}`;

      // Descargar archivo
      saveAs(response.data, filename);
      toast.success(`Archivo ${format.toUpperCase()} descargado exitosamente`);
    } catch (err) {
      console.error('Error al exportar:', err);
      const errorMessage = err.response?.data?.error || 'Error al exportar los datos';
      toast.error(errorMessage);
    }
  };

  return (
    <MainContainer>
      <MainHeader>
        <Title>
          <PublicIcon style={{ marginRight: 8 }} />
          Geolocalización
        </Title>
        <MainHeaderButtonsWrapper>
          <ButtonGroup variant="contained" color="primary">
            <Button
              onClick={() => handleExport('csv')}
              startIcon={<DownloadIcon />}
            >
              Exportar CSV
            </Button>
            <Button
              onClick={() => handleExport('xlsx')}
              startIcon={<DownloadIcon />}
            >
              Exportar Excel
            </Button>
          </ButtonGroup>
        </MainHeaderButtonsWrapper>
      </MainHeader>

      <Paper className={classes.mainPaper} variant="outlined">
        <DateRangeFilter
          onChange={handleDateChange}
          initialStart={dateRange.start}
          initialEnd={dateRange.end}
        />

        {loading ? (
          <Box className={classes.loadingContainer}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <Box className={classes.chartTypeButtons}>
              <ButtonGroup size="small">
                <Button
                  variant={chartType === 'bar' ? 'contained' : 'outlined'}
                  color="primary"
                  onClick={() => setChartType('bar')}
                >
                  Gráfico de Barras
                </Button>
                <Button
                  variant={chartType === 'pie' ? 'contained' : 'outlined'}
                  color="primary"
                  onClick={() => setChartType('pie')}
                >
                  Gráfico de Pastel
                </Button>
              </ButtonGroup>
            </Box>

            <ChartVisualization
              data={statistics}
              type={chartType}
              height={400}
            />

            <StatisticsTable
              data={statistics}
              loading={loading}
            />
          </>
        )}
      </Paper>
    </MainContainer>
  );
};

export default GeolocationPage;
